import React, {
  HTMLAttributes,
  PropsWithChildren,
  useEffect,
  useCallback,
} from 'react';
import { useFocusTrap } from 'app/hooks';
import { useDialog } from '../../dialog.context';
import { isSafeOutsideTarget } from '../../guards/dialog-outside-guard';
import { dialogStack } from '../../../_core/dialog-stack';
import { StyledOverlay } from './overlay.styled';

export const Overlay = (
  props: PropsWithChildren<HTMLAttributes<HTMLDivElement>>
) => {
  const { open, closing, close } = useDialog();
  const ref = useFocusTrap(open);

  useEffect(() => {
    if (!open) {
      return;
    }

    dialogStack.push(close);

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') {
        return;
      }

      if (!dialogStack.isTop(close)) {
        return;
      }

      event.stopPropagation();
      event.preventDefault();
      close();
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      dialogStack.pop(close);
    };
  }, [open, close]);

  const handleOverlayClick = useCallback(
    (e: React.MouseEvent) => {
      const target = e.target;

      if (isSafeOutsideTarget(target)) {
        return;
      }

      if (target === e.currentTarget) {
        close();
      }
    },
    [close]
  );

  if (!open && !closing) {
    return null;
  }

  return (
    <StyledOverlay
      ref={ref}
      $closing={closing}
      onClick={handleOverlayClick}
      {...props}
    />
  );
};

Overlay.displayName = 'Dialog.Overlay';
